import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { HomiLogo } from '@/components/HomiLogo';
import { Check, Crown, Sparkles } from 'lucide-react';
import { http, formatError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const TIERS = [
  {
    id: 'agent',
    name: 'Agent',
    price: 39,
    tagline: 'For the solo agent on the rise.',
    features: [
      'Today Dashboard',
      'Client management (up to 100)',
      'Lead Shield — AI qualification',
      'Deal Desk — full pipeline view',
      '25 AI drafts / month',
      'Email support',
    ],
  },
  {
    id: 'agent-pro',
    name: 'Agent Pro',
    tier: 'agent_pro',
    price: 79,
    tagline: 'For the closer chasing $10M+.',
    highlight: true,
    features: [
      'Everything in Agent',
      'Unlimited clients & deals',
      'Unlimited AI drafts',
      'Listing Legend — MLS copy',
      'Contract Clerk — 1-page summaries',
      'Neighborhood Intel briefs',
      'Priority AI queue',
      'Concierge onboarding',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    price: 149,
    tagline: 'For brokerages & luxury teams.',
    features: [
      'Everything in Agent Pro',
      'Up to 10 seats',
      'Team pipeline view',
      'Brokerage-wide analytics',
      'Custom AI voice tuning',
      'API access',
      'Dedicated success partner',
    ],
  },
];

export default function Pricing() {
  const [checkoutTier, setCheckoutTier] = useState(null); // tier id being processed
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'cancelled') {
      toast('Checkout cancelled. Your membership is unchanged.', { icon: '×' });
      const clean = location.pathname;
      window.history.replaceState({}, '', clean);
    }
  }, [location]);

  const startCheckout = async (tier) => {
    // tier is 'agent' | 'agent_pro' | 'team'
    if (!user) {
      // preserve intent so login can bounce back
      try { sessionStorage.setItem('post_login_checkout_tier', tier); } catch (_) { /* ignore */ }
      navigate('/login?next=/pricing');
      return;
    }
    setCheckoutTier(tier);
    try {
      const { data } = await http.post('/create-checkout-session', { tier });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to start checkout.');
        setCheckoutTier(null);
      }
    } catch (e) {
      toast.error(formatError(e));
      setCheckoutTier(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--ivory)' }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'var(--line)' }}>
        <div className="max-w-6xl mx-auto px-8 py-6 flex justify-between items-center">
          <Link to="/"><HomiLogo size={36} /></Link>
          <nav className="flex gap-6 items-center text-xs uppercase tracking-widest" style={{ color: 'var(--ink-soft)' }}>
            <Link to="/login" style={{ color: 'var(--sage)' }} data-testid="signin-link">Sign In</Link>
            <Link to="/login" className="hq-btn-primary" data-testid="join-link">Join HomiHQ</Link>
          </nav>
        </div>
      </header>

      {/* Founding banner */}
      <div style={{ background: 'var(--sage)' }} className="text-center py-3">
        <div className="max-w-6xl mx-auto px-8 flex items-center justify-center gap-3 text-xs uppercase tracking-widest text-white">
          <Crown size={13} style={{ color: 'var(--gold)' }} />
          <span>Founding Offer — <strong className="font-serif text-base" style={{ color: 'var(--gold)' }}>$49/month for life</strong></span>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto text-center px-8 pt-24 pb-16 hq-fade-in">
        <p className="hq-eyebrow">Membership</p>
        <h1 className="hq-h1 hq-page-title text-4xl sm:text-5xl md:text-6xl mt-4" data-testid="pricing-title">Choose Your Reign.</h1>
        <div className="hq-divider-gold mx-auto w-fit"></div>
        <p className="mt-8 text-lg" style={{ color: 'var(--ink-soft)', lineHeight: 1.7, letterSpacing: '0.02em' }}>
          HomiHQ isn&apos;t software. It&apos;s the AI employee that wakes before you do —
          observing your business, preparing every action, awaiting only your approval.
        </p>
      </section>

      {/* Founding Offer Feature Card */}
      <section className="max-w-6xl mx-auto px-8 mb-12 hq-fade-in-delay-1">
        <div
          className="hq-card p-10 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #FBF6EC 0%, #F4EADE 100%)', border: '1px solid var(--gold)' }}
          data-testid="founding-card"
        >
          <div className="flex items-start justify-between gap-8 flex-wrap">
            <div className="flex-1 min-w-[320px]">
              <div className="flex items-center gap-2 mb-3">
                <Crown size={16} style={{ color: 'var(--gold)' }} />
                <span className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>Founding Members</span>
              </div>
              <h2 className="hq-h1 text-4xl">$49 <span className="font-sans text-xl" style={{ color: 'var(--muted)' }}>/ month · for life</span></h2>
              <p className="mt-4 text-base" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
                Lock in the founding price on Agent Pro — <strong>$79 → $49</strong> — forever.
              </p>
              <ul className="mt-4 space-y-2 text-sm" style={{ color: 'var(--ink)' }}>
                <li className="flex items-center gap-2"><span className="hq-dot"></span>Agent Pro tier — full feature set</li>
                <li className="flex items-center gap-2"><span className="hq-dot"></span>Price locked lifetime, no increases</li>
                <li className="flex items-center gap-2"><span className="hq-dot"></span>Direct line to founding team</li>
                <li className="flex items-center gap-2"><span className="hq-dot"></span>Named on the Founders Wall</li>
              </ul>
            </div>
            <div className="flex flex-col items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => startCheckout('agent_pro')}
                disabled={checkoutTier === 'agent_pro'}
                className="hq-btn-primary"
                data-testid="claim-founding-btn"
              >
                <Sparkles size={12} className="inline mr-2" />
                {checkoutTier === 'agent_pro' ? 'Redirecting…' : 'Claim Founding Seat'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="max-w-6xl mx-auto px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <div
              key={t.id}
              data-testid={`tier-${t.id}`}
              className={`hq-card p-10 flex flex-col hq-fade-in-delay-${i + 1} relative`}
              style={t.highlight ? { border: '1px solid var(--gold)', background: '#FBF6EC' } : {}}
            >
              {t.highlight && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 hq-eyebrow"
                  style={{ background: 'var(--gold)', color: '#fff', borderRadius: 2 }}
                >
                  Most Chosen
                </div>
              )}
              <div>
                <h3 className="hq-h1 text-3xl">{t.name}</h3>
                <p className="hq-eyebrow mt-1">{t.tagline}</p>
                <div className="mt-6 mb-6" style={{ borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)', padding: '1.25rem 0' }}>
                  <span className="font-serif text-5xl" style={{ color: 'var(--sage)' }}>${t.price}</span>
                  <span className="ml-2 text-sm" style={{ color: 'var(--muted)' }}>/ month</span>
                </div>
              </div>
              <ul className="space-y-3 flex-1 mb-8">
                {t.features.map(f => (
                  <li key={f} className="flex items-start gap-3 text-sm" style={{ color: 'var(--ink-soft)' }}>
                    <Check size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--gold)' }} strokeWidth={2} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                className={t.highlight ? 'hq-btn-primary text-center' : 'hq-btn-gold text-center'}
                data-testid={`select-${t.id}-fallback`}
                style={{ display: 'none' }}
              >
                {t.highlight ? 'Begin Membership' : 'Choose ' + t.name}
              </Link>
              <button
                type="button"
                onClick={() => startCheckout(t.tier || t.id)}
                disabled={checkoutTier === (t.tier || t.id)}
                className={t.highlight ? 'hq-btn-primary text-center' : 'hq-btn-gold text-center'}
                data-testid={`select-${t.id}`}
              >
                {checkoutTier === (t.tier || t.id)
                  ? 'Redirecting…'
                  : (t.highlight ? 'Begin Membership' : 'Choose ' + t.name)}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs mt-10" style={{ color: 'var(--muted)', letterSpacing: '0.06em' }}>
          Secure checkout · Cancel anytime · 14-day trial
        </p>
      </section>

      <footer style={{ borderTop: '1px solid var(--line)' }}>
        <div className="max-w-6xl mx-auto px-8 py-8 flex flex-wrap justify-between items-center gap-4 text-xs" style={{ color: 'var(--muted)' }}>
          <div className="tracking-widest uppercase">HomiHQ · Powered by Hired · 2026</div>
          <div className="tracking-widest uppercase">Quiet luxury for the elite agent.</div>
        </div>
      </footer>
    </div>
  );
}
